import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportError } from "../lib/reportError.js";
import { getLanguage, translate } from "../lib/i18n/store.js";

interface Props {
  children: ReactNode;
}

interface State {
  crashed: boolean;
}

/**
 * Top-level crash guard. A render or lifecycle error anywhere below turns into a
 * friendly recover screen instead of a blank white page, and is reported to
 * /api/errors so it reaches the triage agent. Async errors (event handlers,
 * promises) don't hit React's boundary — the window handlers in reportError
 * cover those.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { crashed: false };

  static getDerivedStateFromError(): State {
    return { crashed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError(error, {
      route: "react-render",
      extra: { componentStack: info.componentStack ?? undefined }
    });
  }

  private readonly handleReload = (): void => {
    window.location.reload();
  };

  private readonly handleHome = (): void => {
    window.location.assign("/");
  };

  render(): ReactNode {
    if (!this.state.crashed) return this.props.children;
    return (
      <div className="crash-screen" role="alert">
        <div className="crash-card">
          <h1 className="crash-title">{translate(getLanguage(), "error.crashTitle")}</h1>
          <p className="crash-body">
            {translate(getLanguage(), "error.crashBody")}
          </p>
          <div className="crash-actions">
            <button type="button" className="crash-btn crash-btn-primary" onClick={this.handleReload}>
              {translate(getLanguage(), "error.reloadButton")}
            </button>
            <button type="button" className="crash-btn crash-btn-ghost" onClick={this.handleHome}>
              {translate(getLanguage(), "error.goHomeButton")}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
